import { useEffect, useState } from "react";
import api from "../constant/api";
import { motion, AnimatePresence } from "framer-motion";
import Calendar from "react-calendar";
import moment from "moment";
import "react-calendar/dist/Calendar.css";
import { FaPlus, FaCalendarAlt } from "react-icons/fa";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF";
import ReceiptPDF from "./ReceiptPDF";
import CreditNotePDF from "./CreditNotePDF";
import { toast } from "react-toastify";
import {
  Wallet, Receipt, FileMinus, ShoppingBag, X,
  Send, TrendingUp, AlertCircle, CheckCircle2
} from "lucide-react";

const emptyPaymentForm = { amount: "", payment_method: "cash", reference_no: "", payment_date: new Date().toISOString().slice(0, 10), notes: "" };
const emptyCreditNoteForm = { amount: "", reason: "", invoice_id: "" };

export default function Customers() {

  const email = localStorage.getItem("userEmail");
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "admin" || role === "superadmin";

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showAdd, setShowAdd] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const [todayCount, setTodayCount] = useState(0);
  const [reminderList, setReminderList] = useState([]);

  // ---- LEDGER / BALANCE STATE ----
  const [balances, setBalances] = useState({}); // phone -> {total_invoiced, total_paid, total_credited, balance}
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [ledgerTab, setLedgerTab] = useState("invoices");
  const [ledgerInvoices, setLedgerInvoices] = useState([]);
  const [ledgerOrders, setLedgerOrders] = useState([]);
  const [ledgerPayments, setLedgerPayments] = useState([]);
  const [ledgerCreditNotes, setLedgerCreditNotes] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [showAddCreditNote, setShowAddCreditNote] = useState(false);
  const [creditNoteForm, setCreditNoteForm] = useState(emptyCreditNoteForm);
  const [busy, setBusy] = useState(false);

  // ---- ADMIN: outstanding overview + pending reminders ----
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [runningReminders, setRunningReminders] = useState(false);

  const itemsPerPage = 5;
  const today = moment().format("YYYY-MM-DD");

  const handleCall = (phone) => {
    window.open(`tel:${phone}`);
  };

  const emptyForm = {
    name: "",
    phone: "",
    gst_no: "",
    description: "",
    followup_date: "",
    visit_date: "",
    status: "Pending"
  };

  const handleVisitConfirm = async (c) => {
    const current = Number(c.visit_confirm);
    const newValue = current === 1 ? 0 : 1;

    try {
      await api.post(`Product/updateVisitConfirm/${c.customer_id}`, {
        visit_confirm: newValue
      });

      setCustomers(prev =>
        prev.map(item =>
          item.customer_id === c.customer_id
            ? { ...item, visit_confirm: newValue }
            : item
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const [formData, setFormData] = useState(emptyForm);

  // ================= FETCH =================
  useEffect(() => {
    fetchData();
    loadBalances();
    if (isAdmin) loadPendingReminders();
  }, []);

  const fetchData = () => {
    api.get("Product/getCustomers").then(res => {
      const data = res.data.data || [];
      setCustomers(data);

      setTodayCount(data.filter(c => c.visit_date === today).length);
      setReminderList(data.filter(c => c.followup_date === today));
    });
  };

  const loadBalances = () => {
    api.get("/CustomerLedger/getAllBalances")
      .then(res => res.data.success && setBalances(res.data.data || {}))
      .catch(() => {});
  };

  const loadPendingReminders = () => {
    api.get("/CustomerLedger/getPendingReminders")
      .then(res => res.data.success && setPendingReminders(res.data.data || []))
      .catch(() => {});
  };

  // ================= SEARCH =================
  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  // ================= PAGINATION =================
  const indexOfLast = currentPage * itemsPerPage;
  const currentData = filtered.slice(indexOfLast - itemsPerPage, indexOfLast);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // ================= FORMAT DATE =================
  const formatDate = (date) => {
    if (!date) return "-";
    return moment(date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("DD-MM-YYYY");
  };

  // ================= STATUS COLOR =================
  const statusColor = (s) =>
    s === "Done" ? "bg-green-500" :
    s === "Missed" ? "bg-red-500" :
    s === "Enquiry" ? "bg-gray-500" :
    "bg-yellow-500";

  const isToday = (d) => d === today;

  // ================= WHATSAPP (follow-up, unchanged) =================
  const sendWhatsApp = (phone, name) => {
    const msg = `Hello ${name}, Follow-up reminder from AK Ceramic World`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`);
  };

  // ================= WHATSAPP (balance reminder — NEW) =================
  const handleSendBalanceReminder = async (c) => {
    try {
      const res = await api.post("/CustomerLedger/sendReminder", { phone: c.phone, customer_name: c.name });
      if (res.data.success) {
        if (res.data.auto_sent) {
          toast.success("Reminder sent automatically via WhatsApp API");
        } else {
          window.open(res.data.wa_link, "_blank");
          toast.info("Opened WhatsApp — send the pre-filled message manually");
        }
      } else {
        toast.error(res.data.msg);
      }
    } catch {
      toast.error("Failed to send reminder");
    }
  };

  const handleRunWeeklyNow = async () => {
    setRunningReminders(true);
    try {
      const res = await api.post("/CustomerLedger/runWeeklyReminders");
      if (res.data.success) {
        toast.success(res.data.msg);
        loadPendingReminders();
      } else {
        toast.error(res.data.msg);
      }
    } catch {
      toast.error("Failed to run weekly reminders");
    } finally {
      setRunningReminders(false);
    }
  };

  const handleMarkReminderSent = async (id) => {
    try {
      await api.put(`/CustomerLedger/markReminderSent/${id}`);
      loadPendingReminders();
    } catch {
      toast.error("Failed to update");
    }
  };

  // ================= ADD =================
  const handleAdd = () => {
    const payload = {
      ...formData,
      followup_date: formData.followup_date,
      visit_date: formData.visit_date
    };

    api.post("Product/addCustomer", payload).then(() => {
      fetchData();
      setShowAdd(false);
      setFormData(emptyForm);
    });
  };

  // ================= EDIT =================
  const openEdit = (c) => {
    setEditCustomer(c);

    setFormData({
      ...c,
      followup_date: c.followup_date
        ? moment(c.followup_date, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
        : "",
      visit_date: c.visit_date
        ? moment(c.visit_date, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
        : ""
    });
  };

  // ================= UPDATE =================
  const handleUpdate = () => {
    const payload = {
      ...formData,
      followup_date: formData.followup_date,
      visit_date: formData.visit_date
    };

    api.post(`Product/updateCustomer/${editCustomer.customer_id}`, payload)
      .then(() => {
        fetchData();
        setEditCustomer(null);
      });
  };

  // ================= CALENDAR MARK =================
  const tileContent = ({ date }) => {
    const d = moment(date).format("YYYY-MM-DD");
    const match = customers.find(c => c.followup_date === d);
    if (match) return <div className="text-red-500 text-xs">🔔</div>;
  };

  // ================= LEDGER PANEL =================
  const openLedger = async (c) => {
    setLedgerCustomer(c);
    setLedgerTab("invoices");
    setLedgerLoading(true);
    try {
      const [inv, ord, pay, cn] = await Promise.all([
        api.get(`/CustomerLedger/getInvoices/${c.phone}`),
        api.get(`/CustomerLedger/getOrders/${c.phone}`),
        api.get(`/CustomerLedger/getPayments/${c.phone}`),
        api.get(`/CustomerLedger/getCreditNotes/${c.phone}`)
      ]);
      setLedgerInvoices(inv.data.data || []);
      setLedgerOrders(ord.data.data || []);
      setLedgerPayments(pay.data.data || []);
      setLedgerCreditNotes(cn.data.data || []);
    } catch {
      toast.error("Failed to load customer ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedger = () => {
    setLedgerCustomer(null);
    setShowAddPayment(false);
    setShowAddCreditNote(false);
    setPaymentForm(emptyPaymentForm);
    setCreditNoteForm(emptyCreditNoteForm);
  };

  const refreshLedger = () => {
    if (ledgerCustomer) openLedger(ledgerCustomer);
    loadBalances();
  };

  const currentBalance = ledgerCustomer ? (balances[ledgerCustomer.phone]?.balance || 0) : 0;

  const handleAddPayment = async () => {
    if (!paymentForm.amount) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const res = await api.post("/CustomerLedger/addPayment", {
        email,
        customer_phone: ledgerCustomer.phone,
        customer_name: ledgerCustomer.name,
        ...paymentForm
      });
      if (res.data.success) {
        toast.success("Payment recorded ✅");
        setShowAddPayment(false);
        setPaymentForm(emptyPaymentForm);
        refreshLedger();
      } else toast.error(res.data.msg);
    } catch {
      toast.error("Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const handleAddCreditNote = async () => {
    if (!creditNoteForm.amount) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const res = await api.post("/CustomerLedger/addCreditNote", {
        email,
        customer_phone: ledgerCustomer.phone,
        customer_name: ledgerCustomer.name,
        invoice_id: creditNoteForm.invoice_id || null,
        amount: creditNoteForm.amount,
        reason: creditNoteForm.reason
      });
      if (res.data.success) {
        toast.success("Credit note created ✅");
        setShowAddCreditNote(false);
        setCreditNoteForm(emptyCreditNoteForm);
        refreshLedger();
      } else toast.error(res.data.msg);
    } catch {
      toast.error("Failed to create credit note");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId) => {
    try {
      const res = await api.get(`/order/getInvoice/${invoiceId}`);
      const blob = await pdf(<InvoicePDF invoice={res.data.invoice} items={res.data.items} />).toBlob();
      window.open(URL.createObjectURL(blob));
    } catch {
      toast.error("Failed to generate invoice PDF");
    }
  };

  const handleDownloadReceiptPdf = async (payment) => {
    try {
      const blob = await pdf(
        <ReceiptPDF
          payment={payment}
          customer={{ name: ledgerCustomer.name, phone: ledgerCustomer.phone, address: ledgerCustomer.address }}
          balanceAfter={currentBalance}
        />
      ).toBlob();
      window.open(URL.createObjectURL(blob));
    } catch {
      toast.error("Failed to generate receipt PDF");
    }
  };

  const handleDownloadCreditNotePdf = async (cn) => {
    try {
      const blob = await pdf(
        <CreditNotePDF
          creditNote={cn}
          customer={{ name: ledgerCustomer.name, phone: ledgerCustomer.phone, address: ledgerCustomer.address }}
        />
      ).toBlob();
      window.open(URL.createObjectURL(blob));
    } catch {
      toast.error("Failed to generate credit note PDF");
    }
  };

  // ---- outstanding totals for the admin summary card ----
  const outstandingList = Object.entries(balances)
    .map(([phone, b]) => ({ phone, ...b }))
    .filter(b => b.balance > 0)
    .sort((a, b) => b.balance - a.balance);
  const totalOutstanding = outstandingList.reduce((sum, b) => sum + b.balance, 0);

  return (
    <div className="p-4">

      {/* DASHBOARD */}
      <div className="bg-blue-500 text-white p-4 rounded-xl mb-4 shadow">
        <h2>Today Visits</h2>
        <p className="text-3xl font-bold">{todayCount}</p>
      </div>

      {/* ================= ADMIN: OUTSTANDING BALANCE SUMMARY ================= */}
      {isAdmin && (
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-4 mb-4">
          <div
            className="flex items-center justify-between flex-wrap gap-3 cursor-pointer"
            onClick={() => setShowOutstanding(!showOutstanding)}
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                <TrendingUp size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Outstanding Balance</p>
                <p className="text-xl font-bold text-red-600">₹{totalOutstanding.toLocaleString("en-IN")}</p>
                <p className="text-xs text-gray-400">{outstandingList.length} customer(s) with dues</p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleRunWeeklyNow(); }}
              disabled={runningReminders}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl shadow text-sm disabled:opacity-50 transition"
            >
              <Send size={14} /> {runningReminders ? "Running…" : "Run Weekly Reminders Now"}
            </button>
          </div>

          <AnimatePresence>
            {showOutstanding && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {outstandingList.map((b) => (
                    <div key={b.phone} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-gray-700">{b.phone}</span>
                      <span className="font-semibold text-red-600">₹{b.balance.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  {outstandingList.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No outstanding balances 🎉</p>
                  )}
                </div>

                {pendingReminders.length > 0 && (
                  <div className="mt-4 border-t border-gray-100 pt-3">
                    <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <AlertCircle size={13} className="text-amber-500" /> Pending reminders this week ({pendingReminders.length}) — no WhatsApp API configured, send manually:
                    </p>
                    <div className="space-y-2">
                      {pendingReminders.map((r) => (
                        <div key={r.id} className="flex items-center justify-between text-sm bg-amber-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="font-medium text-gray-800">{r.customer_name || r.customer_phone}</p>
                            <p className="text-xs text-gray-500">₹{Number(r.balance_amount).toLocaleString("en-IN")}</p>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={r.wa_link}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => handleMarkReminderSent(r.id)}
                              className="bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                            >
                              Send
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* REMINDER */}
      <AnimatePresence>
        {reminderList.length > 0 && (
          <motion.div
            initial={{ y: -80 }}
            animate={{ y: 0 }}
            exit={{ y: -80 }}
            className="bg-yellow-400 p-3 mb-4 rounded shadow"
          >
            🔔 Today Follow-ups: {reminderList.length}
          </motion.div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <input
          placeholder="Search name or phone..."
          value={search}
          onChange={(e)=>setSearch(e.target.value)}
          className="border p-2.5 rounded-xl w-full sm:w-64 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />

        <div className="flex gap-2 shrink-0">
          <button
            onClick={()=>setShowCalendar(!showCalendar)}
            className="flex items-center justify-center gap-2 bg-purple-500 text-white px-3 py-2.5 rounded-xl text-sm shadow-sm active:scale-95 transition"
          >
            <FaCalendarAlt />
            <span className="hidden sm:inline">Calendar</span>
          </button>

          <button
            onClick={()=>setShowAdd(true)}
            className="hidden md:flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl shadow-sm transition"
          >
            <FaPlus size={12} /> Add Customer
          </button>
        </div>
      </div>

      {/* CALENDAR */}
      {showCalendar && (
        <div className="bg-white p-4 rounded-xl shadow mb-4">
          <Calendar tileContent={tileContent} />
        </div>
      )}

      {/* DESKTOP TABLE */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow">
        <table className="w-full min-w-[860px]">
          <thead className="bg-gray-100">
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Follow Date</th>
              <th>Status</th>
              <th>Follow Status</th>
              <th>Balance</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {currentData.map(c => {
              const bal = balances[c.phone]?.balance || 0;
              return (
              <tr key={c.customer_id}
                className={`text-center border-t ${
                  isToday(c.followup_date) && "bg-yellow-100 animate-pulse"
                }`}
              >
                <td>{c.name}</td>
                <td>{c.phone}</td>
                <td>{formatDate(c.followup_date)}</td>

                <td>
                  <span className={`text-white px-2 py-1 rounded ${statusColor(c.status)}`}>
                    {c.status}
                  </span>
                </td>
                <td>
                  <span className={`text-xs px-2 py-1 rounded text-white ${
                    c.visit_confirm === 1 ? "bg-green-500" : "bg-red-500"
                  }`}>
                    {c.visit_confirm === 1 ? "Visited" : "Not Visited"}
                  </span>
                </td>
                <td>
                  {bal > 0 ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-semibold">₹{bal.toLocaleString("en-IN")}</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold">Clear</span>
                  )}
                </td>

                <td className="p-2">
                  <div className="flex gap-1.5 justify-center flex-wrap">
                    <button onClick={() => handleCall(c.phone)} className="bg-blue-500 text-white px-2 py-1 rounded text-xs">📞</button>
                    <button onClick={()=>sendWhatsApp(c.phone,c.name)} className="bg-green-500 px-2 py-1 text-white rounded text-xs">💬</button>
                    <button onClick={()=>openEdit(c)} className="bg-yellow-500 px-2 py-1 text-white rounded text-xs">✏️</button>
                    <button onClick={()=>openLedger(c)} className="bg-indigo-600 hover:bg-indigo-700 px-2 py-1 text-white rounded text-xs flex items-center gap-1">
                      <Wallet size={12} /> Ledger
                    </button>
                    {bal > 0 && (
                      <button onClick={()=>handleSendBalanceReminder(c)} className="bg-emerald-600 hover:bg-emerald-700 px-2 py-1 text-white rounded text-xs" title="Send balance reminder">
                        <Send size={12} />
                      </button>
                    )}
                    <div
                      onClick={() => handleVisitConfirm(c)}
                      className={`relative w-14 h-6 flex items-center rounded-full px-1 cursor-pointer transition
                        ${Number(c.visit_confirm) === 1 ? "bg-green-500" : "bg-gray-300"}
                      `}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow transform transition
                          ${Number(c.visit_confirm) === 1 ? "translate-x-8" : ""}
                        `}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* MOBILE */}
      <div className="md:hidden flex flex-col gap-3">
        {currentData.map(c => {
          const bal = balances[c.phone]?.balance || 0;
          return (
          <div
            key={c.customer_id}
            className={`bg-white p-4 rounded-xl shadow ${
              isToday(c.followup_date) && "border-2 border-yellow-400"
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-sm">{c.name}</h3>
                <p className="text-sm">{c.phone}</p>
                <p className="text-xs text-gray-500">
                  {formatDate(c.followup_date)}
                </p>
              </div>
              {bal > 0 ? (
                <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-semibold shrink-0">₹{bal.toLocaleString("en-IN")}</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold shrink-0">Clear</span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className={`text-white px-2 py-1 rounded text-xs ${statusColor(c.status)}`}
              >
                {c.status}
              </span>

              <span
                className={`text-xs px-2 py-1 rounded text-white ${
                  c.visit_confirm === 1 ? "bg-green-500" : "bg-red-500"
                }`}
              >
                {c.visit_confirm === 1 ? "Visited" : "Not Visited"}
              </span>
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button
                onClick={() => handleCall(c.phone)}
                className="bg-blue-500 text-white px-3 py-2 rounded text-xs"
              >
                📞
              </button>

              <button
                onClick={() => sendWhatsApp(c.phone, c.name)}
                className="flex-1 min-w-[70px] bg-green-500 text-white py-2 rounded text-xs"
              >
                WhatsApp
              </button>

              <button
                onClick={() => openEdit(c)}
                className="flex-1 min-w-[60px] bg-yellow-500 text-white py-2 rounded text-xs"
              >
                Edit
              </button>

              <div
                onClick={() => handleVisitConfirm(c)}
                className={`relative w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition shrink-0
                  ${Number(c.visit_confirm) === 1 ? "bg-green-500" : "bg-gray-300"}
                `}
              >
                <div
                  className={`bg-white w-5 h-5 rounded-full shadow transform transition
                    ${Number(c.visit_confirm) === 1 ? "translate-x-6" : ""}
                  `}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => openLedger(c)}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
              >
                <Wallet size={13} /> View Ledger
              </button>
              {bal > 0 && (
                <button
                  onClick={() => handleSendBalanceReminder(c)}
                  className="bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                >
                  <Send size={13} /> Remind
                </button>
              )}
            </div>
          </div>
        );})}
      </div>

      {/* FLOAT BUTTON */}
      <button
        onClick={()=>setShowAdd(true)}
        className="fixed bottom-6 right-5 bg-blue-500 text-white w-14 h-14 flex items-center justify-center rounded-full shadow-xl md:hidden z-40"
      >
        <FaPlus />
      </button>

      {/* PAGINATION */}
      <div className="flex flex-wrap justify-center mt-4 gap-2 pb-20 md:pb-4">
        {[...Array(totalPages)].map((_,i)=>(
          <button key={i}
            onClick={()=>setCurrentPage(i+1)}
            className={`px-3 py-1.5 rounded-lg border text-sm ${
              currentPage===i+1 ? "bg-blue-500 text-white border-blue-500": "border-gray-300"
            }`}
          >
            {i+1}
          </button>
        ))}
      </div>

      {/* ================= ADD MODAL ================= */}
      <AnimatePresence>
        {showAdd && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-[90]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={()=>setShowAdd(false)}
            />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[100]"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
            >
              <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex justify-center pt-3 sm:hidden shrink-0">
                  <div className="w-14 h-1.5 bg-gray-300 rounded-full" />
                </div>
                <div className="px-5 pt-3 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
                  <h2 className="text-lg font-bold text-gray-800">Add Customer</h2>
                </div>

                <div className="p-5 space-y-1 overflow-y-auto flex-1">
                  <input className="input" placeholder="Name"
                    value={formData.name}
                    onChange={e=>setFormData({...formData,name:e.target.value})} />

                  <input className="input" placeholder="Phone"
                    value={formData.phone}
                    onChange={e=>setFormData({...formData,phone:e.target.value})} />

                  <input className="input" placeholder="Gst No"
                    value={formData.gst_no}
                    onChange={e=>setFormData({...formData,gst_no:e.target.value})} />

                  <textarea className="input" placeholder="Description"
                    value={formData.description}
                    onChange={e=>setFormData({...formData,description:e.target.value})} />

                  <input type="date" className="input"
                    value={formData.followup_date || ""}
                    onChange={e=>setFormData({...formData,followup_date:e.target.value})} />
                </div>

                <div className="flex gap-2 p-4 border-t border-gray-100 shrink-0 bg-white">
                  <button onClick={handleAdd}
                    className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold active:scale-95 transition">
                    Save
                  </button>
                  <button onClick={()=>setShowAdd(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold active:scale-95 transition">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= EDIT MODAL ================= */}
      <AnimatePresence>
        {editCustomer && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-[90]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={()=>setEditCustomer(null)}
            />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[100]"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
            >
              <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex justify-center pt-3 sm:hidden shrink-0">
                  <div className="w-14 h-1.5 bg-gray-300 rounded-full" />
                </div>
                <div className="px-5 pt-3 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
                  <h2 className="text-lg font-bold text-gray-800">Edit Customer</h2>
                </div>

                <div className="p-5 space-y-1 overflow-y-auto flex-1">
                  <input className="input" placeholder="Name"
                    value={formData.name}
                    onChange={e=>setFormData({...formData,name:e.target.value})} />

                  <input className="input" placeholder="Phone"
                    value={formData.phone}
                    onChange={e=>setFormData({...formData,phone:e.target.value})} />

                  <input className="input" placeholder="Gst NO"
                    value={formData.gst_no}
                    onChange={e=>setFormData({...formData,gst_no:e.target.value})} />

                  <textarea className="input" placeholder="Description"
                    value={formData.description}
                    onChange={e=>setFormData({...formData,description:e.target.value})} />

                  <input type="date" className="input"
                    value={formData.followup_date || ""}
                    onChange={e=>setFormData({...formData,followup_date:e.target.value})} />

                  <select className="input"
                    value={formData.status}
                    onChange={e=>setFormData({...formData,status:e.target.value})}>
                    <option>Pending</option>
                    <option>Done</option>
                    <option>Missed</option>
                    <option>Enquiry</option>
                  </select>
                </div>

                <div className="flex gap-2 p-4 border-t border-gray-100 shrink-0 bg-white">
                  <button onClick={handleUpdate}
                    className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold active:scale-95 transition">
                    Update
                  </button>
                  <button onClick={()=>setEditCustomer(null)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold active:scale-95 transition">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= CUSTOMER LEDGER PANEL ================= */}
      <AnimatePresence>
        {ledgerCustomer && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-[110]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={closeLedger}
            />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[120]"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
            >
              <div className="bg-white w-full sm:max-w-2xl sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[94vh] sm:max-h-[88vh] flex flex-col overflow-hidden">

                <div className="flex justify-center pt-3 sm:hidden shrink-0">
                  <div className="w-14 h-1.5 bg-gray-300 rounded-full" />
                </div>

                {/* HEADER */}
                <div className="px-5 pt-3 sm:pt-5 pb-4 border-b border-gray-100 shrink-0 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{ledgerCustomer.name}</h2>
                    <p className="text-sm text-gray-500">{ledgerCustomer.phone}</p>
                  </div>
                  <button onClick={closeLedger} className="text-gray-400 hover:text-gray-700 shrink-0">
                    <X size={22} />
                  </button>
                </div>

                <div className="overflow-y-auto flex-1">

                  {/* BALANCE SUMMARY */}
                  <div className="p-5 pb-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-blue-50 rounded-xl p-3 text-center">
                        <p className="text-[10px] text-gray-500">Invoiced</p>
                        <p className="font-bold text-blue-700 text-sm">₹{(balances[ledgerCustomer.phone]?.total_invoiced || 0).toLocaleString("en-IN")}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 text-center">
                        <p className="text-[10px] text-gray-500">Paid</p>
                        <p className="font-bold text-green-700 text-sm">₹{(balances[ledgerCustomer.phone]?.total_paid || 0).toLocaleString("en-IN")}</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-3 text-center">
                        <p className="text-[10px] text-gray-500">Credited</p>
                        <p className="font-bold text-purple-700 text-sm">₹{(balances[ledgerCustomer.phone]?.total_credited || 0).toLocaleString("en-IN")}</p>
                      </div>
                      <div className={`rounded-xl p-3 text-center ${currentBalance > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
                        <p className="text-[10px] text-gray-500">Balance Due</p>
                        <p className={`font-bold text-sm ${currentBalance > 0 ? "text-red-700" : "text-emerald-700"}`}>₹{currentBalance.toLocaleString("en-IN")}</p>
                      </div>
                    </div>

                    {currentBalance > 0 && (
                      <button
                        onClick={() => handleSendBalanceReminder(ledgerCustomer)}
                        className="mt-3 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-semibold transition"
                      >
                        <Send size={14} /> Send WhatsApp Balance Reminder
                      </button>
                    )}
                  </div>

                  {/* TABS */}
                  <div className="px-5 flex gap-1.5 flex-wrap border-b border-gray-100 pb-3">
                    {[
                      { key: "invoices", label: "Invoices", icon: Receipt },
                      { key: "orders", label: "Orders", icon: ShoppingBag },
                      { key: "payments", label: "Payments", icon: Wallet },
                      { key: "creditnotes", label: "Credit Notes", icon: FileMinus }
                    ].map(t => (
                      <button
                        key={t.key}
                        onClick={() => setLedgerTab(t.key)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                          ledgerTab === t.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <t.icon size={13} /> {t.label}
                      </button>
                    ))}
                  </div>

                  <div className="p-5">
                    {ledgerLoading ? (
                      <p className="text-center text-gray-400 text-sm py-8">Loading…</p>
                    ) : (
                      <>
                        {/* INVOICES TAB */}
                        {ledgerTab === "invoices" && (
                          <div className="space-y-2">
                            {ledgerInvoices.map(inv => (
                              <div key={inv.invoice_id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                                <div>
                                  <p className="font-semibold text-sm text-gray-800">{inv.invoice_no}</p>
                                  <p className="text-xs text-gray-400">{inv.created_at?.slice(0, 10)} · {inv.status || "Pending"}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-sm text-gray-700">₹{Number(inv.net_total || 0).toLocaleString("en-IN")}</p>
                                  <button onClick={() => handleDownloadInvoicePdf(inv.invoice_id)} className="bg-blue-600 text-white text-xs px-2.5 py-1.5 rounded-lg">PDF</button>
                                </div>
                              </div>
                            ))}
                            {ledgerInvoices.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No invoices yet.</p>}
                          </div>
                        )}

                        {/* ORDERS TAB */}
                        {ledgerTab === "orders" && (
                          <div className="space-y-2">
                            {ledgerOrders.map(o => (
                              <div key={o.order_id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                                <div>
                                  <p className="font-semibold text-sm text-gray-800">{o.order_no || `#${o.order_id}`}</p>
                                  <p className="text-xs text-gray-400">{o.created_at?.slice(0, 10)} · {o.status || "Pending"}</p>
                                </div>
                                <p className="font-bold text-sm text-gray-700">₹{Number(o.net_total || o.total_amount || 0).toLocaleString("en-IN")}</p>
                              </div>
                            ))}
                            {ledgerOrders.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No orders yet.</p>}
                          </div>
                        )}

                        {/* PAYMENTS TAB */}
                        {ledgerTab === "payments" && (
                          <div>
                            <button
                              onClick={() => setShowAddPayment(true)}
                              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-semibold mb-3 transition"
                            >
                              <FaPlus size={11} /> Record Payment
                            </button>
                            <div className="space-y-2">
                              {ledgerPayments.map(p => (
                                <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                                  <div>
                                    <p className="font-semibold text-sm text-gray-800 capitalize">{p.payment_method}{p.reference_no ? ` · ${p.reference_no}` : ""}</p>
                                    <p className="text-xs text-gray-400">{p.payment_date?.slice(0, 10)}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold text-sm text-green-700">₹{Number(p.amount).toLocaleString("en-IN")}</p>
                                    <button onClick={() => handleDownloadReceiptPdf(p)} className="bg-blue-600 text-white text-xs px-2.5 py-1.5 rounded-lg">Receipt</button>
                                  </div>
                                </div>
                              ))}
                              {ledgerPayments.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No payments recorded yet.</p>}
                            </div>
                          </div>
                        )}

                        {/* CREDIT NOTES TAB */}
                        {ledgerTab === "creditnotes" && (
                          <div>
                            {isAdmin && (
                              <button
                                onClick={() => setShowAddCreditNote(true)}
                                className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl text-sm font-semibold mb-3 transition"
                              >
                                <FaPlus size={11} /> New Credit Note
                              </button>
                            )}
                            <div className="space-y-2">
                              {ledgerCreditNotes.map(cn => (
                                <div key={cn.id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                                  <div>
                                    <p className="font-semibold text-sm text-gray-800">{cn.credit_note_no}</p>
                                    <p className="text-xs text-gray-400">{cn.reason || "—"}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold text-sm text-purple-700">₹{Number(cn.amount).toLocaleString("en-IN")}</p>
                                    <button onClick={() => handleDownloadCreditNotePdf(cn)} className="bg-blue-600 text-white text-xs px-2.5 py-1.5 rounded-lg">PDF</button>
                                  </div>
                                </div>
                              ))}
                              {ledgerCreditNotes.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No credit notes yet.</p>}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= ADD PAYMENT MODAL ================= */}
      <AnimatePresence>
        {showAddPayment && (
          <>
            <motion.div className="fixed inset-0 bg-black/60 z-[130]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAddPayment(false)} />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[140]"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
            >
              <div className="bg-white w-full sm:max-w-sm sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-center pt-3 sm:hidden shrink-0"><div className="w-14 h-1.5 bg-gray-300 rounded-full" /></div>
                <div className="px-5 pt-3 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
                  <h2 className="text-lg font-bold text-gray-800">Record Payment</h2>
                </div>

                <div className="p-5 space-y-3 overflow-y-auto flex-1">
                  <select className="input" value={paymentForm.payment_method} onChange={e => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="online">Online</option>
                  </select>
                  <input type="number" placeholder="Amount (₹)" className="input" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                  {paymentForm.payment_method !== "cash" && (
                    <input placeholder="Reference / Transaction ID" className="input" value={paymentForm.reference_no} onChange={e => setPaymentForm({ ...paymentForm, reference_no: e.target.value })} />
                  )}
                  <input type="date" className="input" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
                  <textarea placeholder="Notes (optional)" className="input" value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
                </div>

                <div className="flex gap-2 p-4 border-t border-gray-100 shrink-0 bg-white">
                  <button onClick={handleAddPayment} disabled={busy} className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold active:scale-95 transition disabled:opacity-50">
                    {busy ? "Saving…" : "Save Payment"}
                  </button>
                  <button onClick={() => setShowAddPayment(false)} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold active:scale-95 transition">Cancel</button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= ADD CREDIT NOTE MODAL ================= */}
      <AnimatePresence>
        {showAddCreditNote && (
          <>
            <motion.div className="fixed inset-0 bg-black/60 z-[130]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAddCreditNote(false)} />
            <motion.div
              className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[140]"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
            >
              <div className="bg-white w-full sm:max-w-sm sm:mx-4 rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-center pt-3 sm:hidden shrink-0"><div className="w-14 h-1.5 bg-gray-300 rounded-full" /></div>
                <div className="px-5 pt-3 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
                  <h2 className="text-lg font-bold text-gray-800">New Credit Note</h2>
                </div>

                <div className="p-5 space-y-3 overflow-y-auto flex-1">
                  <select className="input" value={creditNoteForm.invoice_id} onChange={e => setCreditNoteForm({ ...creditNoteForm, invoice_id: e.target.value })}>
                    <option value="">No specific invoice</option>
                    {ledgerInvoices.map(inv => <option key={inv.invoice_id} value={inv.invoice_id}>{inv.invoice_no}</option>)}
                  </select>
                  <input type="number" placeholder="Credit amount (₹)" className="input" value={creditNoteForm.amount} onChange={e => setCreditNoteForm({ ...creditNoteForm, amount: e.target.value })} />
                  <textarea placeholder="Reason" className="input" value={creditNoteForm.reason} onChange={e => setCreditNoteForm({ ...creditNoteForm, reason: e.target.value })} />
                </div>

                <div className="flex gap-2 p-4 border-t border-gray-100 shrink-0 bg-white">
                  <button onClick={handleAddCreditNote} disabled={busy} className="flex-1 bg-purple-600 text-white py-3 rounded-xl font-semibold active:scale-95 transition disabled:opacity-50">
                    {busy ? "Saving…" : "Create Credit Note"}
                  </button>
                  <button onClick={() => setShowAddCreditNote(false)} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold active:scale-95 transition">Cancel</button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
